import type { AxiosInstance, AxiosError } from 'axios'
import { isErrorStatus } from '@audience-republic/audience-manager-shared/api/utils'
import type { CustomApiError } from '@audience-republic/audience-manager-shared/api/types'
import type {
  Event,
  EventAudienceStatistics,
  EventForServer,
  EventTicketStats,
  EventGroup,
  EventGroupForServer,
  EventGroupSearchParams,
  EventGroupSearchResponse,
  AddEventsToGroupRequest,
  UpcomingEventsSearchParams,
  UpcomingEventsResponse,
  ArchivedEventsResponse,
  BatchArchiveEventsResponse,
  BatchUnarchiveEventsResponse,
  BatchArchiveEventGroupsResponse,
  BatchUnarchiveEventGroupsResponse,
  BatchEventGroupsRequest,
} from '@/types/resources/event'

export default (axios: AxiosInstance) => ({
  /**
   * Get the Event of the event with the given eventOid
   * @param promoterOid
   * @param eventOid
   * @returns
   */
  async get(promoterOid: number, eventOid: number): Promise<Event> {
    const { data, status } = await axios.get(`/promoter/${promoterOid}/event/${eventOid}`, {
      params: {
        $select: 'name,description,campaigns,meta,capacity,location,startDate,endDate,timeZone,derivedStartTime,derivedEndTime,tourOid,presentation,resources,lastImport,ticket-stats,paymentInfo,provider,archivedAt',
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Get Event error',
        message: `Error getting Event from event with oid of ${eventOid}`,
        status,
      }
      throw apiError
    }
    return data
  },
  async search(
    promoterOid: number,
    {
      top = 10,
      skip = 0,
      searchString = null,
      orderby = 'oid desc',
      select = 'name,description,campaigns,meta,capacity,location,startDate,endDate,timeZone,tourOid,presentation,resources,ticket-stats,paymentInfo,provider,type,archivedAt',
      filter = null,
    }: { top: number, skip: number, searchString?: string | null, select?: string, orderby?: string | null, filter?: string | null },
  ): Promise<Event[]> {
    const filters = [
      'userDefined',
    ]
    if (searchString) {
      filters.push(`name ILIKE "%${searchString}%"`)
    }
    if (filter) {
      filters.push(filter)
    }
    const { data, status }: { data: Event[], status: number } = await axios.get(`/promoter/${promoterOid}/event`, {
      params: {
        $select: select,
        $top: top,
        $skip: skip,
        $orderby: orderby,
        $filter: filters.join(' AND '),
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch All Events error',
        message: `Error fetching all Events`,
        status,
      }
      throw apiError
    }
    return data
  },
  async fetchByOids(
    promoterOid: number,
    oids: number[],
    select: string,
  ): Promise<Event[]> {
    if (oids.length === 0) {
      return []
    }
    const { data, status }: { data: Event[], status: number } = await axios.get(`/promoter/${promoterOid}/event`, {
      params: {
        $select: select,
        $filter: oids.map(oid => `oid = ${oid}`).join(' OR '),
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch events with oids error',
        message: `Error fetching events with oids`,
        status,
      }
      throw apiError
    }
    return data
  },
  async patch(promoterOid: number, eventOid: number, event: EventForServer): Promise<void> {
    const { status } = await axios.patch(`/promoter/${promoterOid}/event/${eventOid}`, event)
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Patch Event error',
        message: `Error patching Event with oid of ${eventOid}`,
        status,
      }
      throw apiError
    }
  },
  async clone(promoterOid: number, eventOid: number): Promise<Event> {
    const { status, data } = await axios.post(`/promoter/${promoterOid}/event/${eventOid}/clone`)
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Clone Event error',
        message: `Error cloning Event with oid of ${eventOid}`,
        status,
      }
      throw apiError
    }
    return data
  },
  /**
   * Connect Event with Zoom
   * @param promoterOid
   * @param eventOid
   * @returns
   */
  async connectToZoom(promoterOid: number, eventOid: number, integrationOid: number, accountId: string): Promise<void> {
    const { status } = await axios.patch(`/promoter/${promoterOid}/event/${eventOid}`, {
      provider: 'zoom',
      meta: {
        integrationOid,
        accountId,
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Connect Zoom error',
        message: `Error connecting Zoom to Event with oid of ${eventOid}`,
        status,
      }
      throw apiError
    }
  },
  async getTicketStats(promoterOid: number, eventOid: number, params: object): Promise<EventTicketStats | null> {
    const { data, status } = await axios.get(
      `/promoter/${promoterOid}/event/${eventOid}/ticket-sales-stats-with-filtering/${eventOid}`, { params },
    )

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Get Event error',
        message: `Error getting Ticket Stats from event with oid of ${eventOid}`,
        status,
      }
      throw apiError
    }
    return data
  },
  async getTicketSales(promoterOid: number, eventOid: number, params: object): Promise<EventTicketStats | null> {
    const { data, status } = await axios.get(
      `/promoter/${promoterOid}/event/${eventOid}/ticket-sales-stats-time-series-with-filtering`, { params },
    )

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Get Event error',
        message: `Error getting Ticket Stats from event with oid of ${eventOid}`,
        status,
      }
      throw apiError
    }
    return data
  },
  async fetchCount(
    promoterOid: number,
    {
      filter = null,
    }: {
      filter?: string | null
    }): Promise<{ rows: Event[], count: number } | null> {
    const { status, data } = await axios.get(`/promoter/${promoterOid}/event`, {
      params: {
        $select: 'oid',
        $top: 0,
        $count: true,
        $filter: filter,
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch Events error',
        message: `Error fetching Events`,
        status,
      }
      throw apiError
    }
    return data
  },
  /**
   * Fetch Events
   */
  async fetchAll(
    promoterOid: number,
    {
      top = 20,
      skip = 0,
      orderby = 'sysCtime desc',
      filter = null,
      count = false,
    }: {
      top?: number | null
      skip?: number | null
      orderby?: string | null
      filter?: string | null
      count?: boolean | null
    }): Promise<{ rows: Event[], count: number } | null> {
    if (!top) top = 20
    if (!skip) skip = 0

    const { status, data } = await axios.get(`/promoter/${promoterOid}/event`, {
      params: {
        $select: 'name,description,campaigns,meta,capacity,location,startDate,endDate,timeZone,derivedStartTime,derivedEndTime,tourOid,presentation,resources,lastImport,ticket-stats,paymentInfo,provider,archivedAt',
        $top: top,
        $skip: skip,
        $count: count,
        $orderby: orderby,
        $filter: filter,
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch Events error',
        message: `Error fetching Events`,
        status,
      }
      throw apiError
    }
    return data
  },

  async deleteEventPosOrders(promoterOid: number, eventOid: number): Promise<void> {
    const { status } = await axios.delete(`/promoter/${promoterOid}/event-pos-orders/${eventOid}`)

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Delete POS Orders error',
        message: `Error deleting POS Orders for ${promoterOid} from event with oid of ${eventOid}`,
        status,
      }
      throw apiError
    }
  },

  async deleteEventTicketSales(promoterOid: number, eventOid: number): Promise<void> {
    const { status } = await axios.delete(`/promoter/${promoterOid}/event-ticket-sales/${eventOid}`)

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Delete Ticket Sales error',
        message: `Error deleting ticket sales for ${promoterOid} from event with oid of ${eventOid}`,
        status,
      }
      throw apiError
    }
  },

  async fetchEventAudienceStatistics(promoterOid: number, eventOid: number): Promise<EventAudienceStatistics[] | null> {
    const { status, data } = await axios.get(`/promoter/${promoterOid}/event/${eventOid}/audience-stats`, {})
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch Events error',
        message: `Error fetching Events`,
        status,
      }
      throw apiError
    }
    return data.map((item: EventAudienceStatistics) => {
      let cleanName
      switch (item.audience) {
        case 'suggested':
          cleanName = 'Suggested'
          break
        case 'not-contacted':
          cleanName = 'Not Contacted'
          break
        case 'not-engaged':
          cleanName = 'Not Engaged'
          break
        case 'engaged':
          cleanName = 'Engaged'
          break
        case 'highly-engaged':
          cleanName = 'Highly Engaged'
          break
        case 'purchased':
          cleanName = 'Purchased'
          break
        default:
          cleanName = 'Audience'
      }
      item.cleanName = cleanName
      return item
    })
  },

  async refreshEventAudienceStatistics(promoterOid: number, eventOid: number): Promise<void> {
    const { status } = await axios.post(`/promoter/${promoterOid}/event/${eventOid}/audience-stats`, {})
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Patch Event Audience Stats error',
        message: `Error patching Event Audience Stats with oid of ${eventOid}`,
        status,
      }
      throw apiError
    }
  },

  // Event Groups API methods

  /**
   * Get existing Event Groups with search and pagination support
   * @param promoterOid
   * @param params Search and pagination parameters
   * @returns Promise<EventGroupSearchResponse>
   */
  async searchEventGroups(
    promoterOid: number,
    params: EventGroupSearchParams = {},
  ): Promise<EventGroupSearchResponse> {
    const {
      top = 20,
      skip = 0,
      searchString = null,
      orderby = 'sysCtime desc',
    } = params

    const queryParams: any = {
      // Use 'resource' to match backend EventGroupSnapshot definition (singular join to Bucket)
      $select: 'oid,name,resource,sysCtime,sysMtime,promoterOid,count,archivedAt,startDate,endDate',
      $top: top,
      $skip: skip,
      $orderby: orderby,
      $count: true,
    }

    if (searchString) {
      queryParams.$filter = `name ILIKE "%${searchString}%"`
    }

    const { data, status } = await axios.get(`/promoter/${promoterOid}/event-group`, {
      params: queryParams,
    })

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Search Event Groups error',
        message: `Error searching Event Groups for promoter ${promoterOid}`,
        status,
      }
      throw apiError
    }

    return data
  },

  /**
   * Create a new empty Event Group
   * @param promoterOid
   * @param eventGroup Event group data (name and optional artwork)
   * @returns Promise<EventGroup>
   */
  async createEventGroup(
    promoterOid: number,
    eventGroup: EventGroupForServer,
  ): Promise<EventGroup> {
    const { data, status } = await axios.post(`/promoter/${promoterOid}/event-group`, eventGroup)

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Create Event Group error',
        message: `Error creating Event Group for promoter ${promoterOid}`,
        status,
      }
      throw apiError
    }

    return data
  },

  /**
   * Update an existing Event Group
   * @param promoterOid
   * @param eventGroupOid
   * @param eventGroup Updated event group data
   * @returns Promise<EventGroup>
   */
  async updateEventGroup(
    promoterOid: number,
    eventGroupOid: number,
    eventGroup: EventGroupForServer,
  ): Promise<EventGroup> {
    const { data, status } = await axios.patch(`/promoter/${promoterOid}/event-group/${eventGroupOid}`, eventGroup)

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Update Event Group error',
        message: `Error updating Event Group ${eventGroupOid} for promoter ${promoterOid}`,
        status,
      }
      throw apiError
    }

    return data
  },

  /**
   * Add events to an existing Event Group
   * @param promoterOid
   * @param eventGroupOid
   * @param request List of event OIDs to add
   * @returns Promise<void>
   */
  async addEventsToGroup(
    promoterOid: number,
    eventGroupOid: number,
    request: AddEventsToGroupRequest,
  ): Promise<void> {
    const { status } = await axios.post(`/promoter/${promoterOid}/event-group/${eventGroupOid}/event`, request)

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Add Events to Group error',
        message: `Error adding events to Event Group ${eventGroupOid} for promoter ${promoterOid}`,
        status,
      }
      throw apiError
    }
  },

  /**
   * Get a specific Event Group by OID with nested events
   *
   * Fetches the event group metadata and all events in the group.
   * Makes two API calls:
   * 1. GET /event-group/{oid} - for group metadata (name, oid, etc.)
   * 2. GET /event-group/{oid}/event - for events array
   *
   * @param promoterOid - The promoter OID
   * @param eventGroupOid - The event group OID to fetch
   * @returns Promise<EventGroup> with populated events[] array
   */
  async getEventGroup(promoterOid: number, eventGroupOid: number): Promise<EventGroup> {
    // Fetch group metadata (include archived fields for Archive button logic)
    const { data: groupData, status: groupStatus } = await axios.get(
      `/promoter/${promoterOid}/event-group/${eventGroupOid}`,
      {
        params: {
          $select: 'name,artwork,resourceOid,resource,venues,sysCtime,sysMtime,promoterOid,archivedAt,startDate,startTimeZone,endDate,endTimeZone,derivedStartTime,derivedEndTime',
        },
      },
    )

    if (isErrorStatus(groupStatus)) {
      const apiError: CustomApiError = {
        name: 'Get Event Group error',
        message: `Error getting Event Group ${eventGroupOid} for promoter ${promoterOid}`,
        status: groupStatus,
      }
      throw apiError
    }

    // Fetch events in the group
    const { data: eventsData, status: eventsStatus } = await axios.get(
      `/promoter/${promoterOid}/event-group/${eventGroupOid}/event`,
    )

    if (isErrorStatus(eventsStatus)) {
      const apiError: CustomApiError = {
        name: 'Get Event Group Events error',
        message: `Error getting events for Event Group ${eventGroupOid}`,
        status: eventsStatus,
      }
      throw apiError
    }

    // Combine group metadata with events array
    return {
      ...groupData,
      events: eventsData,
    }
  },

  /**
   * Remove events from an Event Group
   * Uses PUT to replace the entire event list (keeps specified events, removes others)
   * @param promoterOid
   * @param eventGroupOid
   * @param eventOids Array of event OIDs to KEEP in the group
   * @returns Promise<void>
   */
  async removeEventsFromGroup(
    promoterOid: number,
    eventGroupOid: number,
    eventOids: number[],
  ): Promise<void> {
    const { status } = await axios.put(
      `/promoter/${promoterOid}/event-group/${eventGroupOid}/event`,
      { 'event-oids': eventOids },
    )

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Remove Events from Group error',
        message: `Error removing events from Event Group ${eventGroupOid} for promoter ${promoterOid}`,
        status,
      }
      throw apiError
    }
  },

  /**
   * Delete an Event Group
   * @param promoterOid
   * @param eventGroupOid
   * @returns Promise<void>
   */
  async deleteEventGroup(promoterOid: number, eventGroupOid: number): Promise<void> {
    const { status } = await axios.delete(`/promoter/${promoterOid}/event-group/${eventGroupOid}`)

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Delete Event Group error',
        message: `Error deleting Event Group ${eventGroupOid} for promoter ${promoterOid}`,
        status,
      }
      throw apiError
    }
  },

  /**
   * Get upcoming events and event groups combined using new endpoint
   * @param promoterOid
   * @param params Search, sorting, pagination, and filtering parameters
   * @returns Promise<UpcomingEventsResponse>
   */
  async getUpcomingEvents(
    promoterOid: number,
    params: UpcomingEventsSearchParams = {},
  ): Promise<UpcomingEventsResponse> {
    const {
      top = 24,
      skip = 0,
      searchString = null,
      orderby = 'sysCtime desc',
      provider = null,
      type = null,
      startDate = null,
      endDate = null,
    } = params

    const queryParams: any = {
      $top: top,
      $skip: skip,
      $orderby: orderby,
      $count: true,
    }
    if (searchString) {
      queryParams.$filter = `name ILIKE "%${searchString}%"`
    }

    if (provider && provider !== 'all') {
      const providerFilter = `provider = "${provider}"`
      queryParams.$filter = queryParams.$filter
        ? `${queryParams.$filter} AND ${providerFilter}`
        : providerFilter
    }

    if (type) {
      const typeFilter = `type = "${type}"`
      queryParams.$filter = queryParams.$filter
        ? `${queryParams.$filter} AND ${typeFilter}`
        : typeFilter
    }

    // Build date filter - only add constraints if custom dates are provided
    if (startDate || endDate) {
      const today = new Date().toISOString().split('T')[0]
      let dateFilter = ''

      if (startDate && endDate) {
        // Validate that the date range makes sense for upcoming events
        // Use the later of startDate or today as the lower bound
        const effectiveStartDate = startDate < today ? today : startDate

        // Adjust dates by ±1 day to make > and < behave inclusively
        const adjustedStartDate = new Date(effectiveStartDate)
        adjustedStartDate.setDate(adjustedStartDate.getDate() - 1)
        const startDateStr = adjustedStartDate.toISOString().split('T')[0]

        const adjustedEndDate = new Date(endDate)
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1)
        const endDateStr = adjustedEndDate.toISOString().split('T')[0]

        dateFilter = `startDate > '${startDateStr}' AND startDate < '${endDateStr}'`
      }
      else if (startDate) {
        // Ensure start date is at least today for upcoming events
        const effectiveStartDate = startDate < today ? today : startDate

        // Subtract 1 day so > works as >= for user
        const adjustedStartDate = new Date(effectiveStartDate)
        adjustedStartDate.setDate(adjustedStartDate.getDate() - 1)
        const startDateStr = adjustedStartDate.toISOString().split('T')[0]

        dateFilter = `startDate > '${startDateStr}'`
      }
      else if (endDate) {
        // End date only: ensure it starts from today
        // Add 1 day to end date so < works as <= for user
        const adjustedEndDate = new Date(endDate)
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1)
        const endDateStr = adjustedEndDate.toISOString().split('T')[0]

        dateFilter = `startDate > '${today}' AND startDate < '${endDateStr}'`
      }

      queryParams.$filter = queryParams.$filter
        ? `${queryParams.$filter} AND ${dateFilter}`
        : dateFilter
    }

    const { data, status } = await axios.get(`/promoter/${promoterOid}/events-upcoming`, {
      params: queryParams,
    })

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Get Upcoming Events error',
        message: `Error getting upcoming events for promoter ${promoterOid}`,
        status,
      }
      throw apiError
    }

    return data
  },

  /**
   * Get past events and event groups combined using new endpoint
   * @param promoterOid
   * @param params Search, sorting, pagination, and filtering parameters
   * @returns Promise<UpcomingEventsResponse>
   */
  async getPastEvents(
    promoterOid: number,
    params: UpcomingEventsSearchParams = {},
  ): Promise<UpcomingEventsResponse> {
    const {
      top = 24,
      skip = 0,
      searchString = null,
      orderby = 'sysCtime desc',
      provider = null,
      type = null,
      startDate = null,
      endDate = null,
    } = params

    const queryParams: any = {
      $top: top,
      $skip: skip,
      $orderby: orderby,
      $count: true,
    }
    if (searchString) {
      queryParams.$filter = `name ILIKE "%${searchString}%"`
    }

    if (provider && provider !== 'all') {
      const providerFilter = `provider = "${provider}"`
      queryParams.$filter = queryParams.$filter
        ? `${queryParams.$filter} AND ${providerFilter}`
        : providerFilter
    }

    if (type) {
      const typeFilter = `type = "${type}"`
      queryParams.$filter = queryParams.$filter
        ? `${queryParams.$filter} AND ${typeFilter}`
        : typeFilter
    }

    // Build date filter - only add constraints if custom dates are provided
    if (startDate || endDate) {
      const today = new Date().toISOString().split('T')[0]
      let dateFilter = ''

      if (startDate && endDate) {
        // Validate that the date range makes sense for past events
        // Use the earlier of endDate or today as the upper bound
        const effectiveEndDate = endDate > today ? today : endDate

        // Adjust dates by ±1 day to make > and < behave inclusively
        const adjustedStartDate = new Date(startDate)
        adjustedStartDate.setDate(adjustedStartDate.getDate() - 1)
        const startDateStr = adjustedStartDate.toISOString().split('T')[0]

        const adjustedEndDate = new Date(effectiveEndDate)
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1)
        const endDateStr = adjustedEndDate.toISOString().split('T')[0]

        dateFilter = `startDate > '${startDateStr}' AND startDate < '${endDateStr}'`
      }
      else if (startDate) {
        // Start date only: ensure it ends at today
        // Subtract 1 day so > works as >= for user
        const adjustedStartDate = new Date(startDate)
        adjustedStartDate.setDate(adjustedStartDate.getDate() - 1)
        const startDateStr = adjustedStartDate.toISOString().split('T')[0]

        dateFilter = `startDate > '${startDateStr}' AND startDate < '${today}'`
      }
      else if (endDate) {
        // Ensure end date is at most today for past events
        const effectiveEndDate = endDate > today ? today : endDate

        // Add 1 day so < works as <= for user
        const adjustedEndDate = new Date(effectiveEndDate)
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1)
        const endDateStr = adjustedEndDate.toISOString().split('T')[0]

        dateFilter = `startDate < '${endDateStr}'`
      }

      queryParams.$filter = queryParams.$filter
        ? `${queryParams.$filter} AND ${dateFilter}`
        : dateFilter
    }

    const { data, status } = await axios.get(`/promoter/${promoterOid}/events-past`, {
      params: queryParams,
    })

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Get Past Events error',
        message: `Error getting past events for promoter ${promoterOid}`,
        status,
      }
      throw apiError
    }

    return data
  },

  /**
   * Get archived events and event groups combined using new endpoint
   * @param promoterOid
   * @param params Search, sorting, pagination, and filtering parameters
   * @returns Promise<ArchivedEventsResponse>
   */
  async getArchivedEvents(
    promoterOid: number,
    params: UpcomingEventsSearchParams = {},
  ): Promise<ArchivedEventsResponse> {
    const {
      top = 24,
      skip = 0,
      searchString = null,
      orderby = 'archivedAt desc',
      provider = null,
      type = null,
    } = params

    const queryParams: any = {
      $top: top,
      $skip: skip,
      $orderby: orderby,
      $count: true,
    }
    if (searchString) {
      queryParams.$filter = `name ILIKE "%${searchString}%"`
    }

    if (provider && provider !== 'all') {
      const providerFilter = `provider = "${provider}"`
      queryParams.$filter = queryParams.$filter
        ? `${queryParams.$filter} AND ${providerFilter}`
        : providerFilter
    }

    if (type) {
      const typeFilter = `type = "${type}"`
      queryParams.$filter = queryParams.$filter
        ? `${queryParams.$filter} AND ${typeFilter}`
        : typeFilter
    }

    const { data, status } = await axios.get(`/promoter/${promoterOid}/events-archived`, {
      params: queryParams,
    })

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Get Archived Events error',
        message: `Error getting archived events for promoter ${promoterOid}`,
        status,
      }
      throw apiError
    }

    return data
  },

  /**
   * Archive a single event
   * @param promoterOid
   * @param eventOid
   * @returns Promise<void>
   */
  async archiveEvent(promoterOid: number, eventOid: number): Promise<void> {
    const { status } = await axios.post(`/promoter/${promoterOid}/event/${eventOid}/archive`)

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Archive Event error',
        message: `Error archiving event ${eventOid}`,
        status,
      }
      throw apiError
    }
  },

  /**
   * Archive an event group
   * @param promoterOid
   * @param eventGroupOid
   * @returns Promise<void>
   */
  async archiveEventGroup(promoterOid: number, eventGroupOid: number): Promise<void> {
    const { status } = await axios.post(`/promoter/${promoterOid}/event-group/${eventGroupOid}/archive`)

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Archive Event Group error',
        message: `Error archiving event group ${eventGroupOid}`,
        status,
      }
      throw apiError
    }
  },

  /**
   * Unarchive a single event
   * @param promoterOid
   * @param eventOid
   * @returns Promise<void>
   */
  async unarchiveEvent(promoterOid: number, eventOid: number): Promise<void> {
    const { status } = await axios.post(`/promoter/${promoterOid}/event/${eventOid}/unarchive`)

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Unarchive Event error',
        message: `Error unarchiving event ${eventOid}`,
        status,
      }
      throw apiError
    }
  },

  /**
   * Unarchive an event group
   * @param promoterOid
   * @param eventGroupOid
   * @returns Promise<void>
   */
  async unarchiveEventGroup(promoterOid: number, eventGroupOid: number): Promise<void> {
    const { status } = await axios.post(`/promoter/${promoterOid}/event-group/${eventGroupOid}/unarchive`)

    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Unarchive Event Group error',
        message: `Error unarchiving event group ${eventGroupOid}`,
        status,
      }
      throw apiError
    }
  },

  // Batch Archive API methods

  async batchArchiveEvents(
    promoterOid: number,
    eventOids: number[],
  ): Promise<BatchArchiveEventsResponse> {
    try {
      const { data } = await axios.post<BatchArchiveEventsResponse>(
        `/promoter/${promoterOid}/batch-archive-events`,
        { 'event-oids': eventOids },
      )
      return data
    }
    catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>
      throw {
        name: 'BatchArchiveEventsError',
        message: axiosError.response?.data?.message || 'Error batch archiving events',
        status: axiosError.response?.status || 500,
      } as CustomApiError
    }
  },

  async batchUnarchiveEvents(
    promoterOid: number,
    eventOids: number[],
  ): Promise<BatchUnarchiveEventsResponse> {
    try {
      const { data } = await axios.post<BatchUnarchiveEventsResponse>(
        `/promoter/${promoterOid}/batch-unarchive-events`,
        { 'event-oids': eventOids },
      )
      return data
    }
    catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>
      throw {
        name: 'BatchUnarchiveEventsError',
        message: axiosError.response?.data?.message || 'Error batch unarchiving events',
        status: axiosError.response?.status || 500,
      } as CustomApiError
    }
  },

  async batchArchiveEventGroups(
    promoterOid: number,
    eventGroupOids: number[],
    forceAsync?: boolean,
  ): Promise<BatchArchiveEventGroupsResponse> {
    const body: BatchEventGroupsRequest = { 'event-group-oids': eventGroupOids }
    if (forceAsync !== undefined) body.async = forceAsync

    try {
      const { data } = await axios.post<BatchArchiveEventGroupsResponse>(
        `/promoter/${promoterOid}/batch-archive-event-groups`,
        body,
      )
      return data
    }
    catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>
      throw {
        name: 'BatchArchiveEventGroupsError',
        message: axiosError.response?.data?.message || 'Error batch archiving event groups',
        status: axiosError.response?.status || 500,
      } as CustomApiError
    }
  },

  async batchUnarchiveEventGroups(
    promoterOid: number,
    eventGroupOids: number[],
    forceAsync?: boolean,
  ): Promise<BatchUnarchiveEventGroupsResponse> {
    const body: BatchEventGroupsRequest = { 'event-group-oids': eventGroupOids }
    if (forceAsync !== undefined) body.async = forceAsync

    try {
      const { data } = await axios.post<BatchUnarchiveEventGroupsResponse>(
        `/promoter/${promoterOid}/batch-unarchive-event-groups`,
        body,
      )
      return data
    }
    catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>
      throw {
        name: 'BatchUnarchiveEventGroupsError',
        message: axiosError.response?.data?.message || 'Error batch unarchiving event groups',
        status: axiosError.response?.status || 500,
      } as CustomApiError
    }
  },

})
